import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { Porch } from "building-types/porches/porch";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { RWTable, Table } from "common/data/table";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { mergedPropLibraryNew } from "doc/propTools";
import { polyLine2Area } from "geom/polyLine2";
import getTargetedPorchSettings from "../elements/porch/getTargetedPorchSettings";
import { generatePorch } from "../elements/porch/porch";
import { RoomIndexImpl } from "../elements/rooms/roomIndexImpl";
import {
  buildInsideRoomBSP,
  collectPorchRooms,
  planarGraphVerticesToPoints,
} from "../generationTools/generatePorchesTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

function select({
  rooms,
  levels,
  walls,
  doc: { assets, settings: docSettings, entitySettings, profiles },
  roomLevelIndices,
  roomsByLevel,
  roomFloorAssemblies,
  planarGraphs,
  settings,
  settingsSets,
  optionChoices,
  planarGraphPerimeters,
  roofSystems: roofs,
  windowUnits,
  porchColumnPatterns,
  mirror,
  enabledFeatures,
}: Building) {
  return {
    rooms,
    levels,
    planarGraphs,
    roomLevelIndices,
    roomsByLevel,
    roomFloorAssemblies,
    walls,
    settings,
    planarGraphPerimeters,
    roofs,
    windowUnits,
    porchColumnPatterns,
    mirror,
    docSettings,
    entitySettings,
    assets,
    settingsSets,
    optionChoices,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles,
      profileSettings: settings.profile,
    },
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  profileSource: "shallow",
});

async function generate({
  rooms,
  levels,
  planarGraphs,
  roomLevelIndices,
  roomsByLevel,
  roomFloorAssemblies,
  walls,
  settings,
  planarGraphPerimeters,
  roofs,
  windowUnits,
  porchColumnPatterns,
  mirror,
  docSettings,
  entitySettings,
  assets,
  settingsSets,
  optionChoices,
  profileSource,
}: ReturnType<typeof select>): Promise<{ porches: Table<Porch>; issues: BuildingGenerationIssue[] }> {
  const mergedPropLibrary = mergedPropLibraryNew(assets);
  const porches: RWTable<Porch> = {};
  const issues: BuildingGenerationIssue[] = [];
  const visitedPorchRooms: { [levelId: string]: { [key: number]: boolean } } = {};
  const roomIndex = new RoomIndexImpl(rooms, roomLevelIndices, roomsByLevel, planarGraphs);
  const targetedPorchSettings = getTargetedPorchSettings(
    { settingsSets, optionChoices, settings, rooms },
    { settings: docSettings, entitySettings },
  );

  const insideRoomBSP = buildInsideRoomBSP(rooms);

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const levelId = room.level.id;

    if (!visitedPorchRooms[levelId]) {
      visitedPorchRooms[levelId] = {};
    }

    if (visitedPorchRooms[levelId][roomLevelIndices[roomId]]) {
      continue;
    }

    const level = room.level;

    const levelWalls = walls[levelId];

    const floorAbovePlanarGraph = level?.levelAbove ? planarGraphs[level.levelAbove] : undefined;
    const { unsupportedPerimeterVertices } = planarGraphPerimeters[levelId];

    const unsupportedPerimeterPoints = planarGraphVerticesToPoints(floorAbovePlanarGraph, unsupportedPerimeterVertices);
    const porchRooms = collectPorchRooms(room, roomIndex, visitedPorchRooms[levelId], targetedPorchSettings);

    let roomPorchSettings: PorchSettingsBase | undefined = undefined;
    let highestArea = 0;

    for (const porchRoom of porchRooms) {
      const porchRoomId = getSourceRoomId(porchRoom);
      if (room.type === RoomType.FRONT_PORCH) {
        const area = polyLine2Area(porchRoom.outline);
        if (area > highestArea) {
          roomPorchSettings = targetedPorchSettings[porchRoomId];
          highestArea = area;
        }
      } else if (room.type === RoomType.REAR_PORCH) {
        const area = polyLine2Area(porchRoom.outline);
        if (area > highestArea) {
          roomPorchSettings = targetedPorchSettings[porchRoomId];
          highestArea = area;
        }
      }
    }

    const rearPorchSettingsIfExists = settings.rearPorch ?? settings.porch;
    const mergedPorchSettings = room.type === RoomType.FRONT_PORCH ? settings.porch : rearPorchSettingsIfExists;

    const resolvedPorchSettings = roomPorchSettings ?? mergedPorchSettings;

    if (room.type === RoomType.FRONT_PORCH || room.type === RoomType.REAR_PORCH) {
      const porch = await generatePorch({
        insideRoomBSP,
        rooms: porchRooms,
        roomFloorAssemblies,
        levels,
        level,
        wallSystem: levelWalls,
        roomIndex,
        unsupportedCornerAbovePts: unsupportedPerimeterPoints,
        porchSettings: resolvedPorchSettings,
        roomType: room.type,
        mirror,
        porchColumnPatterns,
        roofs,
        windowUnits,
        wallSettings: settings.wall,
        columnSettings: settings.column,
        structureSettings: settings.structure,
        propCatalog: settings.props,
        mergedPropLibrary,
        exteriorTrimSettings: settings.exteriorTrim,
        profileSource,
        planSettings: settings.planset,
        foundationSystemSettings: foundationSystemSettingsFromSettingsConfig(settings.foundation),
        targetedPorchSettings,
        issues,
      });
      if (porch) {
        porches[porch.id] = porch;
      }
    }
  }

  return { porches, issues };
}

export default memoAsync(select, generate, compare);
